import { Badge, Button, Card } from "@procurement-agents/ui";
import { constraintsOf, displayEndId, labelsOf, outcomesOf, type FieldChange, type Requisition } from "@procurement-agents/scope-api";

const SOURCE: Record<string, { label: string; tone: "good" | "warn" | "neutral" }> = {
  intake: { label: "from the request", tone: "good" }, inferred: { label: "estimated", tone: "warn" }, unknown: { label: "unknown", tone: "neutral" },
  edited: { label: "edited at review", tone: "good" },   // CHANGE_requisition_review
};

/** A changed value as a line: lists and numbers read plainly, an outcome or key requirement by its id and text. */
export function changeText(v: unknown): string {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" && x && "text" in x ? `${displayEndId(String((x as { id?: string }).id ?? ""))} ${(x as { text: string }).text}`.trim() : String(x))).join("; ") || "—";
  if (typeof v === "object") return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${changeText(x)}`).join("; ");
  return typeof v === "number" ? v.toLocaleString() : String(v);
}

/** CHANGE_requisition_review: who changed what at the requisition review, with the before and after on expand. */
export function RequisitionEdits({ req, labels }: { req: Partial<Requisition>; labels: Record<string, string> }) {
  const edits = req.edits ?? [];
  if (!edits.length) return null;
  const name = (c: FieldChange) => labels[c.field] ?? c.field.replace(/_/g, " ");
  return (
    <details className="requisition-edits small">
      <summary className="muted">Edited by {[...new Set(edits.map((e) => e.by))].join(", ")} · {[...new Set(edits.flatMap((e) => e.changes.map(name)))].join(", ")}</summary>
      <ul>{edits.flatMap((e, i) => e.changes.map((c) => <li key={`${i}-${c.field}`}><strong>{name(c)}</strong>: <span className="muted">{changeText(c.before)}</span> → {changeText(c.after)}</li>))}</ul>
    </details>
  );
}

export function RequisitionView({ req, full, onDownload, labels }: { req: Partial<Requisition>; full: boolean; onDownload?: () => void; labels?: Record<string, string> }) {
  const L = labelsOf(labels);
  const src = req.estimated_value_source ? SOURCE[req.estimated_value_source] : null;
  return (
    <Card title={req.title ?? "Requisition"} aside={<span className="row"><Badge>{req.category}</Badge>{onDownload && <Button variant="quiet" onClick={onDownload}>Download Word</Button>}</span>}>
      <RequisitionEdits req={req} labels={L} />
      <dl className="dl">
        <dt>{L.business_need}</dt><dd>{req.business_need}</dd>
        {/* CHANGE_outcome_traceability: numbered, so people can refer to "o3" in chat and reviews */}
        <dt>{L.desired_outcomes}</dt><dd><ul className="numbered-ends">{outcomesOf(req).map((o) => <li key={o.id}><span className="end-id">{o.id}</span> {o.text}{o.measure && <span className="muted small"> · measure: {o.measure}</span>}</li>)}</ul></dd>
        <dt>{L.scope_summary}</dt><dd>{req.scope_summary}</dd>
        {full && <><dt>{L.estimated_value}</dt><dd>{req.estimated_value != null ? `${req.estimated_value.toLocaleString()} ${req.currency}` : "unknown"} {src && <Badge tone={src.tone}>{src.label}</Badge>}</dd></>}
        <dt>{L.timeframe}</dt><dd>{req.timeframe}</dd>
        {/* CHANGE_requisition_review: key requirements read as R1, R2 … (stored c1, c2 …) */}
        <dt>{L.constraints}</dt><dd><ul className="numbered-ends">{constraintsOf(req).map((c) => <li key={c.id}><span className="end-id">{displayEndId(c.id)}</span> {c.text}</li>)}</ul></dd>
        <dt>{L.stakeholders}</dt><dd><ul>{(req.stakeholders ?? []).map((o, i) => <li key={i}>{o}</li>)}</ul></dd>
        {!!req.review_answers?.length && <><dt>Answered at the review</dt><dd><ul>{req.review_answers.map((a) => <li key={a.question_id}><strong>{a.label}:</strong> {a.answer}</li>)}</ul></dd></>}
        {req.review_note && <><dt>Note from the requester</dt><dd>{req.review_note}</dd></>}
        {full && <><dt>{L.assumptions}</dt><dd><ul>{(req.assumptions ?? []).map((o, i) => <li key={i}>{o}</li>)}</ul></dd>
          <dt>{L.open_questions}</dt><dd><ul>{(req.open_questions ?? []).map((o, i) => <li key={i}>{o}</li>)}</ul></dd></>}
      </dl>
    </Card>
  );
}
